import { ReactNode } from 'react';
import { Flex, theme } from 'antd';
import type { GlobalToken } from 'antd';
import styled from 'styled-components';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';

export type PageTitleProps = {
  /** Page title (rendered as the page's h1). */
  title: string;
  /** Optional description below the title. */
  description?: ReactNode;
  /** Optional actions aligned to the right of the title (e.g. a create button). */
  actions?: ReactNode;
  /** Optional breadcrumb above the title (e.g. a `Breadcrumb`). */
  breadcrumb?: ReactNode;
  className?: string;
};

// Design spec: space between the breadcrumb and the title. Not on the token
// scale (marginXXL is 48).
const BREADCRUMB_GAP = 80;

/**
 * PageTitle — the big title of a page (32px, regular), with an optional
 * breadcrumb above it, a description and actions. The title is the only text
 * in the display font (Poppins); the description is Inter at the base size.
 * Both use the brand ink.
 *
 * @example
 * ```tsx
 * import { Breadcrumb, PageTitle } from '@libertify/ui';
 *
 * <PageTitle
 *   breadcrumb={<Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Tutorials' }]} />}
 *   title="Tutorials"
 *   description="Short video lessons that walk you through the platform."
 * />
 * ```
 */
export const PageTitle = ({
  title,
  description,
  actions,
  breadcrumb,
  className,
}: PageTitleProps) => {
  const { token } = theme.useToken();

  return (
    <Flex vertical gap={BREADCRUMB_GAP} className={className}>
      {breadcrumb}
      <StyledRow $token={token}>
        <StyledText $token={token}>
          <StyledTitle $token={token}>{title}</StyledTitle>
          {description ? (
            <StyledDescription $token={token}>{description}</StyledDescription>
          ) : null}
        </StyledText>
        {actions ? (
          <StyledActions $token={token}>{actions}</StyledActions>
        ) : null}
      </StyledRow>
    </Flex>
  );
};

export default PageTitle;

// ─── Styled components ───

type WithToken = { $token: GlobalToken };

const StyledRow = styled.div<WithToken>`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${({ $token }) => $token.marginLG}px;
  flex-wrap: wrap;
`;

const StyledText = styled.div<WithToken>`
  display: flex;
  flex-direction: column;
  gap: ${({ $token }) => $token.marginXS}px;
  min-width: 0;
`;

// Design spec: page titles are 32px (the typography scale's 32px heading
// size), regular weight, tightly tracked. -0.03em is not on the
// letter-spacing scale.
const TITLE_FONT_SIZE = typography.heading.h3.fontSize;
const TITLE_LETTER_SPACING = '-0.03em';

const StyledTitle = styled.h1<WithToken>`
  margin: 0;
  font-family: ${typography.fontFamily.primary};
  font-size: ${TITLE_FONT_SIZE}px;
  font-weight: ${typography.fontWeight.regular};
  letter-spacing: ${TITLE_LETTER_SPACING};
  line-height: ${({ $token }) => $token.lineHeightHeading2};
  color: ${colors.secondary[700]};
`;

const StyledDescription = styled.div<WithToken>`
  margin: 0;
  font-family: ${typography.fontFamily.secondary};
  font-size: ${({ $token }) => $token.fontSize}px;
  line-height: ${({ $token }) => $token.lineHeight};
  color: ${colors.secondary[700]};
`;

const StyledActions = styled.div<WithToken>`
  display: flex;
  align-items: center;
  gap: ${({ $token }) => $token.paddingSM}px;
  flex-shrink: 0;
`;
